import { useCallback, useEffect, useState } from 'react'
import { Check, Copy, Link2, Lock, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Field, Input, Select } from '@/components/ui/input'
import { Badge, ErrorNote, Spinner } from '@/components/ui/misc'
import { Dialog } from '@/components/ui/overlay'
import { api, ApiError, type ShareLink } from '@/lib/api'
import { formatDateTime, formatRelative } from '@/lib/format'
import { useAuth } from '@/features/auth/AuthProvider'

const EXPIRY_OPTIONS = [
  { value: '24', label: '24 hours' },
  { value: '168', label: '7 days (default)' },
  { value: '720', label: '30 days' },
  { value: '8760', label: '1 year' },
  { value: 'never', label: 'Never expires' },
]

export function ShareDialog({
  bucket,
  objectKey,
  onClose,
}: {
  bucket: string
  objectKey: string
  onClose: () => void
}) {
  const { canEdit } = useAuth()
  const [shares, setShares] = useState<ShareLink[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState<string | null>(null)

  const [expiry, setExpiry] = useState('168')
  const [maxDownloads, setMaxDownloads] = useState('')
  const [password, setPassword] = useState('')

  const load = useCallback(() => {
    setLoading(true)
    api
      .shares({ bucket, key: objectKey })
      .then((res) => setShares(res.shares))
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'Could not load existing links'),
      )
      .finally(() => setLoading(false))
  }, [bucket, objectKey])

  useEffect(load, [load])

  async function create() {
    setCreating(true)
    setError(null)
    try {
      const res = await api.createShare({
        bucket,
        key: objectKey,
        expiresInHours: expiry === 'never' ? null : Number(expiry),
        maxDownloads: maxDownloads ? Number(maxDownloads) : null,
        ...(password ? { password } : {}),
      })
      setShares((current) => [res.share, ...current])
      void copy(res.share.url)
      setPassword('')
      setMaxDownloads('')
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create the link')
    } finally {
      setCreating(false)
    }
  }

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(url)
      window.setTimeout(() => setCopied(null), 2000)
    } catch {
      // Clipboard access can be blocked; the URL is selectable in the field.
    }
  }

  async function revoke(id: string) {
    try {
      await api.revokeShare(id)
      load()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not revoke the link')
    }
  }

  return (
    <Dialog
      open
      size="lg"
      onClose={onClose}
      title="Share this file"
      description="Anyone with the link can download it without signing in."
      footer={
        <Button variant="primary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <div className="space-y-6">
        {error && <ErrorNote>{error}</ErrorNote>}

        {canEdit && (
          <div className="space-y-4 rounded-md border border-border bg-surface p-4">
            <p className="text-sm font-medium">Create a link</p>

            <div className="grid gap-3 sm:grid-cols-3">
              <Field label="Expires" htmlFor="share-expiry">
                <Select
                  id="share-expiry"
                  value={expiry}
                  onChange={(e) => setExpiry(e.target.value)}
                >
                  {EXPIRY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Max downloads" htmlFor="share-max">
                <Input
                  id="share-max"
                  type="number"
                  min={1}
                  placeholder="Unlimited"
                  value={maxDownloads}
                  onChange={(e) => setMaxDownloads(e.target.value)}
                />
              </Field>

              <Field label="Password" htmlFor="share-pw">
                <Input
                  id="share-pw"
                  type="text"
                  placeholder="Optional"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                />
              </Field>
            </div>

            {expiry === 'never' && (
              <p className="text-xs text-warning">
                A link that never expires stays usable until someone revokes it. Prefer an
                expiry unless you genuinely need permanent access.
              </p>
            )}

            <Button variant="primary" onClick={create} loading={creating}>
              <Link2 className="h-4 w-4" />
              Create link
            </Button>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm font-medium">
            Existing links {shares.length > 0 && `(${shares.length})`}
          </p>

          {loading && (
            <div className="flex justify-center py-6">
              <Spinner />
            </div>
          )}

          {!loading && shares.length === 0 && (
            <p className="py-4 text-sm text-muted-foreground">
              No links have been created for this file.
            </p>
          )}

          <ul className="space-y-2">
            {shares.map((share) => (
              <li key={share.id} className="rounded-md border border-border p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex items-center gap-2">
                      <Input
                        readOnly
                        value={share.url}
                        className="h-8 font-mono text-xs"
                        onFocus={(e) => e.currentTarget.select()}
                      />
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => void copy(share.url)}
                        aria-label="Copy link"
                        title="Copy link"
                      >
                        {copied === share.url ? (
                          <Check className="h-4 w-4 text-success" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </Button>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {share.active ? (
                        <Badge tone="success">Active</Badge>
                      ) : (
                        <Badge tone="danger">{share.problem ?? 'Inactive'}</Badge>
                      )}
                      {share.hasPassword && (
                        <Badge tone="neutral">
                          <Lock className="h-3 w-3" />
                          Password
                        </Badge>
                      )}
                      <span>
                        {share.downloadCount}
                        {share.maxDownloads !== null && ` / ${share.maxDownloads}`} download
                        {share.downloadCount === 1 ? '' : 's'}
                      </span>
                      <span>
                        {share.expiresAt
                          ? `expires ${formatDateTime(share.expiresAt)}`
                          : 'never expires'}
                      </span>
                      <span>
                        by {share.createdBy} {formatRelative(share.createdAt)}
                      </span>
                    </div>
                  </div>

                  {canEdit && !share.revokedAt && (
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => void revoke(share.id)}
                      aria-label="Revoke link"
                      title="Revoke this link immediately"
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </Dialog>
  )
}
